/**
 * Mention-Token im Kommentar-Body — Single Source of Truth.
 *
 * Speicher-/Render-Format einer Erwähnung im `body` eines Kommentars: `@[<uuid>]`.
 * Genau dieses Token treibt SOWOHL die UI-Anzeige (oranger Chip via
 * `renderMentionBody`) ALS AUCH die Mention-Benachrichtigung (Server leitet die
 * Empfänger-UUIDs hieraus ab). Body ist damit die einzige Quelle — kein
 * separates `mentions`-Array mehr als Eingabe.
 */

/** Erkennt `@[<uuid>]`-Token (UUID = 36 Zeichen). Global-Flag → stets `lastIndex`-frei nutzen. */
export const MENTION_TOKEN = /@\[([0-9a-fA-F-]{36})\]/g;

/**
 * Extrahiert die erwähnten user_ids (dedupliziert, Reihenfolge des Auftretens)
 * aus einem Kommentar-Body. Stateless: nutzt je Aufruf eine frische RegExp.
 */
export function extractMentions(body: string): string[] {
  const re = new RegExp(MENTION_TOKEN.source, 'g');
  const out = new Set<string>();
  let m: RegExpExecArray | null;
  // biome-ignore lint/suspicious/noAssignInExpressions: idiomatischer exec-Loop
  while ((m = re.exec(body)) !== null) {
    if (m[1]) out.add(m[1]);
  }
  return [...out];
}

/**
 * Ersetzt die `@[<uuid>]`-Token durch `@<Name>` — die lesbare Fassung eines
 * Kommentar-Bodys für alle Ausgabekanäle ohne React (Mail, Digest, Tooltips).
 *
 * Warum hier in `shared` und nicht im jeweiligen Paket (Issue #70): Der Web-Client
 * hatte diese Auflösung längst (`mentionBodyToPlainText`), der Mail-Pfad der API
 * NICHT — dort ging der Roh-Body ungefiltert in die Benachrichtigung, und
 * Empfänger lasen „@[ad95fa7e-1418-40e4-970c-eb1a6daf9670]" statt eines Namens.
 * Eine gemeinsame Regel an einer Stelle ist die Antwort darauf; eine zweite Kopie
 * im API-Paket hätte denselben Drift nur später wiederholt.
 *
 * Fallback für unbekannte IDs (gelöschter Nutzer, ID aus einem anderen Mandanten):
 * die ersten 8 Zeichen der UUID — bewusst identisch zur Web-Variante. Ein leeres
 * `@` wäre schlimmer als eine kurze ID: Es sähe aus wie ein Tippfehler des Autors.
 */
export function resolveMentions(body: string, namesById: Map<string, string>): string {
  return body.replace(new RegExp(MENTION_TOKEN.source, 'g'), (_match: string, id?: string) => {
    const uuid = id ?? '';
    const name = namesById.get(uuid);
    return `@${name && name.trim().length > 0 ? name : uuid.slice(0, 8)}`;
  });
}

/**
 * Kürzt einen bereits aufgelösten Body auf `maxLength` Zeichen (Ellipse angehängt).
 *
 * Getrennt von `resolveMentions`, damit die Reihenfolge im Aufrufer sichtbar bleibt:
 * **erst auflösen, dann kürzen.** Andersherum kann der Schnitt mitten in ein
 * `@[uuid]`-Token fallen; übrig bliebe ein halbes Token, das kein Auflöser mehr
 * erkennt — in der Mail stünde dann eine abgehackte UUID. Da ein aufgelöster Name
 * fast immer KÜRZER ist als sein Token (36+3 Zeichen), verliert diese Reihenfolge
 * zudem weniger Inhalt.
 */
export function truncateBody(body: string, maxLength: number): string {
  return body.length > maxLength ? `${body.slice(0, maxLength)}…` : body;
}
